<template>
  <div class="project-detail-page">
    <WelcomeBanner :title="project.project_name || '项目详情'" :subtitle="project.project_desc || ''">
      <div slot="right">
        <el-button type="text" icon="el-icon-arrow-left" size="small" class="back-button" @click="goBack">返回</el-button>
        <el-button type="primary" @click="editProject">编辑</el-button>
      </div>
    </WelcomeBanner>

    <BaseCard>
      <el-row :gutter="20" class="detail-content">
      <el-col :span="16">
        <!-- 项目基本信息 -->
        <el-card class="info-card">
          <div slot="header" class="card-header">
            <span class="header-title">项目信息</span>
            <el-tag :type="getStatusType(project.status)" size="small">{{ getStatusText(project.status) }}</el-tag>
          </div>
          <div class="basic-info">
            <h2>{{ project.project_name }}</h2>
            <p class="desc">{{ project.project_desc || '暂无描述' }}</p>
            <div class="meta">
              <span><i class="el-icon-user"></i> 负责人：{{ project.owner_name || '-' }}</span>
              <span><i class="el-icon-chat-dot-square"></i> 项目群：{{ project.project_group || '未设置' }}</span>
              <span><i class="el-icon-time"></i> 创建时间：{{ formatTime(project.create_time) }}</span>
              <span v-if="project.update_time"><i class="el-icon-refresh"></i> 更新时间：{{ formatTime(project.update_time) }}</span>
            </div>
          </div>
        </el-card>

        <!-- 任务进度 -->
        <el-card class="task-board" style="margin-top: 20px;">
          <div slot="header" class="card-header">
            <span class="header-title">任务进度</span>
            <el-button type="text" @click="goToTasks">查看全部</el-button>
          </div>
          <div class="progress-summary">
            <div class="stat-box">
              <div class="stat-number">{{ progress.total }}</div>
              <div class="stat-label">总任务</div>
            </div>
            <div class="stat-box">
              <div class="stat-number success">{{ progress.completed }}</div>
              <div class="stat-label">已完成</div>
            </div>
            <div class="stat-box">
              <div class="stat-number primary">{{ progress.percent }}%</div>
              <div class="stat-label">完成率</div>
            </div>
          </div>
          <el-progress 
            :percentage="progress.percent" 
            :color="progressColors"
            :stroke-width="12"
            class="main-progress"
          ></el-progress>
          
          <div class="task-groups">
            <div class="task-group">
              <h4><i class="el-icon-s-flag"></i> 待执行 ({{ tasks.pending.length }})</h4>
              <div v-if="!tasks.pending.length" class="empty-mini">暂无待执行任务</div>
              <div v-else class="task-tags">
                <el-tag 
                  v-for="task in tasks.pending" 
                  :key="task.task_id" 
                  size="small" 
                  effect="plain"
                  class="task-tag"
                >{{ task.task_name }}</el-tag>
              </div>
            </div>
            <div class="task-group">
              <h4><i class="el-icon-success"></i> 已完成 ({{ tasks.completed.length }})</h4>
              <div v-if="!tasks.completed.length" class="empty-mini">暂无已完成任务</div>
              <div v-else class="task-tags">
                <el-tag 
                  v-for="task in tasks.completed" 
                  :key="task.task_id" 
                  type="success" 
                  size="small"
                  effect="plain"
                  class="task-tag"
                >{{ task.task_name }}</el-tag>
              </div>
            </div>
          </div>
        </el-card>

        <!-- 任务时间轴 -->
        <el-card class="task-timeline-card" style="margin-top: 20px;">
          <div slot="header" class="card-header">
            <span class="header-title">任务时间轴</span>
          </div>
          <TaskTimeline :project-id="projectId" />
        </el-card>

        <!-- 最近协作文态 -->
        <el-card class="recent-activity" style="margin-top: 20px;">
          <div slot="header" class="card-header">
            <span class="header-title">最近协作动态</span>
          </div>
          <div v-if="!recentLogs.length" class="empty-text">
            <i class="el-icon-document" style="font-size: 48px; color: #dcdfe6;"></i>
            <p>暂无记录</p>
          </div>
          <div v-else class="log-list">
            <div v-for="log in recentLogs" :key="log.log_id" class="log-item">
              <div class="log-avatar">
                <i class="el-icon-s-custom"></i>
              </div>
              <div class="log-content">
                <div class="log-header">
                  <span class="log-author">{{ log.robot_name }}</span>
                  <span class="log-time">{{ formatTime(log.create_time) }}</span>
                </div>
                <div class="log-action">{{ log.action_desc }}</div>
              </div>
            </div>
          </div>
        </el-card>
      </el-col>

      <el-col :span="8">
        <!-- 项目成员 -->
        <el-card class="members-card">
          <div slot="header" class="card-header">
            <span class="header-title">项目成员</span>
            <span class="member-count">{{ members.length }} 人</span>
          </div>
          <div v-if="!members.length" class="empty-text">
            <i class="el-icon-user-solid" style="font-size: 48px; color: #dcdfe6;"></i>
            <p>暂无成员</p>
          </div>
          <div v-else class="member-list">
            <div v-for="member in members" :key="member.member_id" class="member-item">
              <div class="member-avatar">
                <i :class="member.member_type === 1 ? 'el-icon-user' : 'el-icon-s-tools'"></i>
              </div>
              <div class="member-info">
                <span class="member-name">{{ member.member_name }}</span>
                <el-tag size="mini" :type="getRoleType(member.member_role)">{{ getRoleText(member.member_role) }}</el-tag>
              </div>
            </div>
          </div>
        </el-card>

        <!-- 项目汇报列表 -->
        <el-card class="reports-card" style="margin-top: 20px;">
          <div slot="header" class="card-header">
            <span class="header-title">项目汇报</span>
            <el-button type="text" @click="goToReports">查看全部</el-button>
          </div>
          <div v-if="!reports.length" class="empty-text">
            <i class="el-icon-document-copy" style="font-size: 48px; color: #dcdfe6;"></i>
            <p>暂无汇报</p>
          </div>
          <div v-else class="report-list">
            <div v-for="report in reports" :key="report.summary_id" class="report-item" @click="goToReport(report.summary_id)">
              <div class="report-type">{{ report.summary_type }}</div>
              <div class="report-title">{{ report.title || '无标题' }}</div>
              <div class="report-date">{{ report.summary_date }}</div>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>
    </BaseCard>
  </div>
</template>

<script>
import { getProjectDetail } from '../api/project'
import BaseCard from '@/components/BaseCard'
import WelcomeBanner from '@/components/WelcomeBanner'
import TaskTimeline from '@/components/TaskTimeline'

export default {
  name: 'ProjectDetail',
  components: { BaseCard, WelcomeBanner, TaskTimeline },
  data() {
    return {
      projectId: null,
      project: {},
      progress: {
        total: 0,
        completed: 0,
        percent: 0
      },
      tasks: {
        pending: [],
        completed: []
      },
      members: [],
      recentLogs: [],
      reports: [],
      loading: false,
      progressColors: [
        { color: '#f56c6c', percentage: 20 },
        { color: '#e6a23c', percentage: 40 },
        { color: '#5cb87a', percentage: 60 },
        { color: '#1989fa', percentage: 80 },
        { color: '#67c23a', percentage: 100 }
      ]
    }
  },
  created() {
    this.projectId = this.$route.params.id
    this.loadData()
  },
  methods: {
    async loadData() {
      this.loading = true
      try {
        const data = await getProjectDetail(this.projectId)
        if (data) {
          this.project = data.project || {}
          this.progress = data.progress || { total: 0, completed: 0, percent: 0 }
          this.tasks = data.tasks || { pending: [], completed: [] }
          this.members = data.members || []
          this.recentLogs = data.recentLogs || []
          this.reports = data.reports || []
        }
      } catch (error) {
        console.error(error)
        this.$message.error('获取数据失败')
      } finally {
        this.loading = false
      }
    },
    formatTime(time) {
      if (!time) return '-'
      return new Date(time).toLocaleString('zh-CN')
    },
    goBack() {
      this.$router.push('/projects')
    },
    goToTasks() {
      this.$router.push(`/tasks?project_id=${this.projectId}`)
    },
    goToReports() {
      this.$router.push('/summary-list')
    },
    goToReport(id) {
      this.$router.push(`/summary/${id}`)
    },
    editProject() {
      this.$router.push(`/project/${this.projectId}/edit`)
    },
    getStatusType(status) {
      const map = {
        1: 'success',
        2: 'warning',
        3: 'info'
      }
      return map[status] || 'info'
    },
    getStatusText(status) {
      const map = {
        1: '进行中',
        2: '已暂停',
        3: '已完成'
      }
      return map[status] || '未知'
    },
    getRoleType(role) {
      const map = {
        'owner': 'danger',
        'admin': 'warning',
        'member': ''
      }
      return map[role] || ''
    },
    getRoleText(role) {
      const map = {
        'owner': '拥有者',
        'admin': '管理员',
        'member': '成员'
      }
      return map[role] || role
    }
  }
}
</script>

<style lang="scss" scoped>
.project-detail-page {
  padding: 20px;
  min-height: calc(100vh - 84px);
  background-color: var(--bg-page);
}

.page-header {
  margin-bottom: 20px;
}

.detail-content {
  margin: 0 !important;
}

/* 卡片通用样式 */
.info-card,
.task-board,
.task-timeline-card,
.recent-activity,
.members-card,
.reports-card {
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
}

.member-count {
  font-size: 13px;
  color: var(--text-muted);
}

/* 项目基本信息 */
.basic-info {
  h2 {
    margin: 0 0 12px 0;
    font-size: 24px;
    color: var(--text-primary);
  }

  .desc {
    color: var(--text-secondary);
    line-height: 1.6;
    margin: 0 0 16px 0;
    font-size: 14px;
  }

  .meta {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    color: var(--text-muted);
    font-size: 13px;

    span {
      display: flex;
      align-items: center;
      gap: 4px;
    }

    i {
      font-size: 14px;
    }
  }
}

/* 任务进度 */
.progress-summary {
  display: flex;
  justify-content: space-around;
  margin-bottom: 20px;
  padding: 16px;
  background-color: var(--bg-page);
  border-radius: var(--radius-lg);
}

.stat-box {
  text-align: center;
}

.stat-number {
  font-size: 28px;
  font-weight: 600;
  color: var(--text-primary);

  &.success {
    color: var(--success-color);
  }

  &.primary {
    color: var(--primary-color);
  }
}

.stat-label {
  font-size: 13px;
  color: var(--text-muted);
  margin-top: 4px;
}

.main-progress {
  margin-bottom: 24px;
}

.task-groups {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.task-group {
  h4 {
    margin: 0 0 12px 0;
    font-size: 14px;
    color: var(--text-secondary);
    display: flex;
    align-items: center;
    gap: 6px;

    i {
      font-size: 16px;
    }
  }
}

.task-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.task-tag {
  margin: 0 !important;
}

.empty-mini {
  color: var(--text-disabled);
  font-size: 13px;
  padding: 8px 0;
}

/* 最近协作文态 */
.log-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.log-item {
  display: flex;
  gap: 12px;
  padding: 12px;
  background-color: var(--bg-page);
  border-radius: var(--radius-md);
  border: 1px solid var(--border-light);
}

.log-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: var(--primary-color);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  i {
    color: var(--bg-card);
    font-size: 18px;
  }
}

.log-content {
  flex: 1;
  min-width: 0;
}

.log-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}

.log-author {
  font-weight: 500;
  color: var(--text-primary);
  font-size: 14px;
}

.log-time {
  color: var(--text-muted);
  font-size: 12px;
}

.log-action {
  color: var(--text-secondary);
  font-size: 13px;
  line-height: 1.5;
}

/* 项目成员 */
.member-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.member-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px;
  background-color: var(--bg-page);
  border-radius: var(--radius-md);
  border: 1px solid var(--border-light);
}

.member-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: var(--success-color);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  i {
    color: var(--bg-card);
    font-size: 16px;
  }
}

.member-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.member-name {
  font-weight: 500;
  color: var(--text-primary);
  font-size: 14px;
}

/* 项目汇报 */
.report-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.report-item {
  padding: 12px;
  background-color: var(--bg-page);
  border-radius: var(--radius-md);
  border: 1px solid var(--border-light);
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    background-color: var(--primary-light);
    border-color: var(--primary-color);
  }
}

.report-type {
  display: inline-block;
  padding: 2px 8px;
  background-color: var(--primary-color);
  color: var(--bg-card);
  font-size: 12px;
  border-radius: var(--radius-sm);
  margin-bottom: 6px;
}

.report-title {
  font-weight: 500;
  color: var(--text-primary);
  font-size: 14px;
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.report-date {
  color: var(--text-muted);
  font-size: 12px;
}

/* 空状态 */
.empty-text {
  text-align: center;
  color: var(--text-disabled);
  padding: 40px 20px;

  p {
    margin-top: 12px;
    font-size: 14px;
  }
}
</style>
